import { enumRecordToCheckboxOptions } from '@umss/shared/utils';

export enum ContractorDocumentType {
  DRIVERS_LICENSE = 'DriversLicense',
  W9_FORM = 'W9Form',
  DIRECT_DEPOSIT_FORM = 'DirectDepositForm',
  CONTRACTOR_AGREEMENT = 'ContractorAgreement',
  BLS_CERTIFICATION = 'BlsCertification',
  CPR_CERTIFICATION = 'CprCertification',
  IMMUNIZATION_RECORDS = 'ImmunizationRecords',
  MEDICAL_LICENSE = 'MedicalLicense',
  BACKGROUND_CHECK = 'BackgroundCheck',
  PHOTO_ID = 'PhotoId',
  OTHER_CERTIFICATION = 'OtherCertification',
  I9_FORM = 'I9Form',
  RESUME = 'Resume',
  STATE_BACKGROUND_CHECK = 'StateBackgroundCheck'
}

export const CONTRACTOR_DOCUMENT_TYPE_RECORD: Record<
  ContractorDocumentType,
  string
> = {
  [ContractorDocumentType.DRIVERS_LICENSE]: 'Driver License',
  [ContractorDocumentType.W9_FORM]: 'W9 Form',
  [ContractorDocumentType.DIRECT_DEPOSIT_FORM]: 'Direct Deposit Form',
  [ContractorDocumentType.CONTRACTOR_AGREEMENT]: 'Contractor Agreement',
  [ContractorDocumentType.BLS_CERTIFICATION]: 'BLS Certification',
  [ContractorDocumentType.CPR_CERTIFICATION]: 'CPR Certification',
  [ContractorDocumentType.IMMUNIZATION_RECORDS]: 'Immunization Records',
  [ContractorDocumentType.MEDICAL_LICENSE]: 'Medical License',
  [ContractorDocumentType.BACKGROUND_CHECK]: 'Background Check',
  [ContractorDocumentType.PHOTO_ID]: 'Photo ID',
  [ContractorDocumentType.OTHER_CERTIFICATION]: 'Other Certification',
  [ContractorDocumentType.I9_FORM]: 'I-9 Form',
  [ContractorDocumentType.RESUME]: 'Resume',
  [ContractorDocumentType.STATE_BACKGROUND_CHECK]: 'State Background Check'
};

export type DocumentListCategory =
  | DocumentListCategoryEnum
  | `${DocumentListCategoryEnum}`;

export enum DocumentListCategoryEnum {
  Certifications = 'certifications',
  Documents = 'documents',
  Forms = 'forms',
  Resume = 'resume',
  StateBackgroundCheck = 'stateBackgroundCheck'
}

export const DOCUMENT_LIST_CATEGORY_RECORD: Record<
  DocumentListCategoryEnum,
  string
> = {
  [DocumentListCategoryEnum.Certifications]: 'Certifications',
  [DocumentListCategoryEnum.Documents]: 'Documents',
  [DocumentListCategoryEnum.Forms]: 'Forms',
  [DocumentListCategoryEnum.Resume]: 'Resume',
  [DocumentListCategoryEnum.StateBackgroundCheck]: 'State Background Check'
};

export const DOCUMENT_LIST_CATEGORY_ICONS: Record<
  DocumentListCategoryEnum,
  string
> = {
  [DocumentListCategoryEnum.Certifications]: 'phosphorCertificateBold',
  [DocumentListCategoryEnum.Documents]: 'phosphorFileBold',
  [DocumentListCategoryEnum.Forms]: 'phosphorClipboardTextBold',
  [DocumentListCategoryEnum.Resume]: 'phosphorReadCvLogoBold',
  [DocumentListCategoryEnum.StateBackgroundCheck]: 'phosphorUserCheck'
};

export const DOCUMENT_LIST_CATEGORY_TYPE_RECORD: Record<
  DocumentListCategoryEnum,
  ContractorDocumentType[]
> = {
  [DocumentListCategoryEnum.Certifications]: [
    ContractorDocumentType.BLS_CERTIFICATION,
    ContractorDocumentType.CPR_CERTIFICATION,
    ContractorDocumentType.OTHER_CERTIFICATION,
    ContractorDocumentType.MEDICAL_LICENSE
  ],
  [DocumentListCategoryEnum.Documents]: [
    ContractorDocumentType.BACKGROUND_CHECK,
    ContractorDocumentType.CONTRACTOR_AGREEMENT,
    ContractorDocumentType.IMMUNIZATION_RECORDS,
    ContractorDocumentType.DRIVERS_LICENSE,
    ContractorDocumentType.PHOTO_ID
  ],
  [DocumentListCategoryEnum.Forms]: [
    ContractorDocumentType.DIRECT_DEPOSIT_FORM,
    ContractorDocumentType.W9_FORM,
    ContractorDocumentType.I9_FORM
  ],
  [DocumentListCategoryEnum.Resume]: [ContractorDocumentType.RESUME],
  [DocumentListCategoryEnum.StateBackgroundCheck]: [
    ContractorDocumentType.STATE_BACKGROUND_CHECK
  ]
};

export const CERTIFICATION_TYPE_OPTIONS =
  enumRecordToCheckboxOptions<ContractorDocumentType>(
    CONTRACTOR_DOCUMENT_TYPE_RECORD,
    {
      includeValues:
        DOCUMENT_LIST_CATEGORY_TYPE_RECORD[
          DocumentListCategoryEnum.Certifications
        ],
      sortByLabel: 'includeValuesOrder'
    }
  );

export const DOCUMENT_TYPE_OPTIONS =
  enumRecordToCheckboxOptions<ContractorDocumentType>(
    CONTRACTOR_DOCUMENT_TYPE_RECORD,
    {
      includeValues:
        DOCUMENT_LIST_CATEGORY_TYPE_RECORD[DocumentListCategoryEnum.Documents],
      sortByLabel: 'includeValuesOrder'
    }
  );

export const DOCUMENT_FORMS =
  enumRecordToCheckboxOptions<ContractorDocumentType>(
    CONTRACTOR_DOCUMENT_TYPE_RECORD,
    {
      // Uploadable form types only. I-9 is intentionally excluded — we never
      // collect one — and W-9 is intentionally excluded — tax verification is
      // now handled exclusively via Stripe hosted onboarding. Both remain in
      // DOCUMENT_LIST_CATEGORY_TYPE_RECORD so existing documents are still
      // fetched, displayed and filterable.
      includeValues: [ContractorDocumentType.DIRECT_DEPOSIT_FORM],
      sortByLabel: 'includeValuesOrder'
    }
  );

export const RESUME_DOCUMENT =
  enumRecordToCheckboxOptions<ContractorDocumentType>(
    CONTRACTOR_DOCUMENT_TYPE_RECORD,
    {
      includeValues:
        DOCUMENT_LIST_CATEGORY_TYPE_RECORD[DocumentListCategoryEnum.Resume],
      sortByLabel: 'includeValuesOrder'
    }
  );

export const STATE_BACKGROUND_CHECK_DOCUMENT =
  enumRecordToCheckboxOptions<ContractorDocumentType>(
    CONTRACTOR_DOCUMENT_TYPE_RECORD,
    {
      includeValues:
        DOCUMENT_LIST_CATEGORY_TYPE_RECORD[
          DocumentListCategoryEnum.StateBackgroundCheck
        ],
      sortByLabel: 'includeValuesOrder'
    }
  );

export const DOCUMENT_LIST_CATEGORY_TYPE_OPTIONS: Record<
  DocumentListCategoryEnum,
  { value: ContractorDocumentType; label: string }[]
> = {
  [DocumentListCategoryEnum.Certifications]: CERTIFICATION_TYPE_OPTIONS,
  [DocumentListCategoryEnum.Documents]: DOCUMENT_TYPE_OPTIONS,
  [DocumentListCategoryEnum.Forms]: DOCUMENT_FORMS,
  [DocumentListCategoryEnum.Resume]: RESUME_DOCUMENT,
  [DocumentListCategoryEnum.StateBackgroundCheck]: STATE_BACKGROUND_CHECK_DOCUMENT
};

/**
 * Generates grouped document type options by category.
 * Dynamically maps each category to its options with proper group labels.
 * This ensures scalability - new document types only need to be added to DOCUMENT_LIST_CATEGORY_TYPE.
 */
export const getGroupedDocumentTypeOptions = () => {
  return Object.values(DocumentListCategoryEnum).flatMap((category) => {
    const categoryLabel = DOCUMENT_LIST_CATEGORY_RECORD[category];
    // Built from the category record rather than the upload option lists: this
    // feeds display/filter UIs, which must still offer types that exist on old
    // documents but are no longer uploadable (e.g. I-9).
    return DOCUMENT_LIST_CATEGORY_TYPE_RECORD[category].map((value) => ({
      value,
      label: CONTRACTOR_DOCUMENT_TYPE_RECORD[value],
      group: categoryLabel
    }));
  });
};

/**
 * Defines the category configuration required by the document list.
 * Accepts either the enum value or the string literal representation.
 */

export const CONTRACTOR_DOCUMENT_TYPE_OPTIONS =
  enumRecordToCheckboxOptions<ContractorDocumentType>(
    CONTRACTOR_DOCUMENT_TYPE_RECORD
  );
